import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

// Import exportToSvg for static SVG rendering (view-only mode)
import { exportToSvg } from '@excalidraw/excalidraw';

// Excalidraw element type (simplified for our use case)
interface ExcalidrawElementBase {
    id: string;
    type: string;
    x: number;
    y: number;
    width: number;
    height: number;
    [key: string]: unknown;
}

// AppState type (simplified for our use case)
interface ExcalidrawAppState {
    viewBackgroundColor?: string;
    gridSize?: number | null;
    [key: string]: unknown;
}

// Render state types for better state management
type RenderState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success'; mode: 'interactive' | 'static'; svgContent?: string }
    | { status: 'error'; message: string };

// Excalidraw data structure matching backend output
interface ExcalidrawData {
    type: string;
    version: number;
    source: string;
    elements: ExcalidrawElementBase[];
    appState: Partial<ExcalidrawAppState>;
    files: Record<string, unknown>;
}

// Tiptap Excalidraw Node View Component with resize functionality (View-only mode)
function ExcalidrawNodeView({ node, updateAttributes, selected }: any) {
    const [renderState, setRenderState] = useState<RenderState>({ status: 'idle' });
    const [isResizing, setIsResizing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // Initialize dimensions from node attributes
    const initialWidth = node.attrs.width || 700;
    const initialHeight = node.attrs.height || 400;

    const [dimensions, setDimensions] = useState({
        width: initialWidth,
        height: initialHeight,
    });
    const [aspectRatio, setAspectRatio] = useState(initialWidth / initialHeight);
    const containerRef = useRef<HTMLDivElement>(null);
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const animationFrameRef = useRef<number | null>(null);
    const latestDimensionsRef = useRef({ width: initialWidth, height: initialHeight });

    // Parse the Excalidraw data
    const excalidrawData: ExcalidrawData | null = useMemo(() => {
        try {
            const data = node.attrs.data;
            if (!data) return null;
            return typeof data === 'string' ? JSON.parse(data) : data;
        } catch (e) {
            console.error('[Excalidraw] Failed to parse data:', e);
            return null;
        }
    }, [node.attrs.data]);

    // Generate static SVG for view mode
    const generateStaticSvg = useCallback(async () => {
        if (!excalidrawData || !excalidrawData.elements) {
            setRenderState({ status: 'error', message: 'No diagram data available' });
            return;
        }

        setRenderState({ status: 'loading' });

        try {
            const svg = await exportToSvg({
                elements: excalidrawData.elements as any,
                appState: {
                    ...excalidrawData.appState,
                    exportWithDarkMode: false,
                } as any,
                files: (excalidrawData.files || {}) as any,
            });

            // Make SVG responsive
            svg.setAttribute('width', '100%');
            svg.setAttribute('height', '100%');
            svg.style.maxWidth = '100%';
            svg.style.maxHeight = '100%';

            setRenderState({
                status: 'success',
                mode: 'static',
                svgContent: svg.outerHTML,
            });
        } catch (error) {
            console.error('[Excalidraw] Failed to generate SVG:', error);
            setRenderState({
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to render diagram',
            });
        }
    }, [excalidrawData]);

    // Initialize render - always generate static SVG (view-only mode)
    useEffect(() => {
        if (excalidrawData) {
            generateStaticSvg();
        } else {
            setRenderState({ status: 'error', message: 'No diagram data' });
        }
    }, [excalidrawData, generateStaticSvg]);

    // Cleanup animation frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Resize handlers
    const handleMouseDown = (e: React.MouseEvent, corner: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        resizeStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: dimensions.width,
            height: dimensions.height,
        };

        const handleMouseMove = (moveEvent: MouseEvent) => {
            moveEvent.preventDefault();
            moveEvent.stopPropagation();

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            animationFrameRef.current = requestAnimationFrame(() => {
                const deltaX = moveEvent.clientX - resizeStartRef.current.x;
                const deltaY = moveEvent.clientY - resizeStartRef.current.y;

                let newWidth = resizeStartRef.current.width;
                let newHeight = resizeStartRef.current.height;
                const preserveAspectRatio = !moveEvent.shiftKey;

                if (corner === 'se') {
                    if (preserveAspectRatio) {
                        if (Math.abs(deltaX) > Math.abs(deltaY)) {
                            newWidth = Math.max(300, resizeStartRef.current.width + deltaX);
                            newHeight = Math.max(200, newWidth / aspectRatio);
                        } else {
                            newHeight = Math.max(200, resizeStartRef.current.height + deltaY);
                            newWidth = Math.max(300, newHeight * aspectRatio);
                        }
                    } else {
                        newWidth = Math.max(300, resizeStartRef.current.width + deltaX);
                        newHeight = Math.max(200, resizeStartRef.current.height + deltaY);
                    }
                } else if (corner === 'e') {
                    newWidth = Math.max(300, resizeStartRef.current.width + deltaX);
                    if (preserveAspectRatio) {
                        newHeight = Math.max(200, newWidth / aspectRatio);
                    }
                } else if (corner === 's') {
                    newHeight = Math.max(200, resizeStartRef.current.height + deltaY);
                    if (preserveAspectRatio) {
                        newWidth = Math.max(300, newHeight * aspectRatio);
                    }
                }

                const newDimensions = { width: newWidth, height: newHeight };
                latestDimensionsRef.current = newDimensions;
                setDimensions(newDimensions);
            });
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
            upEvent.preventDefault();
            upEvent.stopPropagation();
            setIsResizing(false);

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            const finalWidth = Math.round(latestDimensionsRef.current.width);
            const finalHeight = Math.round(latestDimensionsRef.current.height);

            updateAttributes({
                width: finalWidth,
                height: finalHeight,
            });

            document.removeEventListener('mousemove', handleMouseMove, true);
            document.removeEventListener('mouseup', handleMouseUp, true);
        };

        document.addEventListener('mousemove', handleMouseMove, true);
        document.addEventListener('mouseup', handleMouseUp, true);
    };

    const textAlign = node.attrs.textAlign || 'left';

    const getMarginStyle = () => {
        switch (textAlign) {
            case 'center':
                return '16px auto';
            case 'right':
                return '16px 0 16px auto';
            default:
                return '16px 0';
        }
    };

    // Render error state
    if (renderState.status === 'error') {
        return (
            <NodeViewWrapper>
                <div
                    className="excalidraw-container excalidraw-error"
                    style={{
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        backgroundColor: '#fef2f2',
                        overflow: 'hidden',
                        maxWidth: '100%',
                        margin: getMarginStyle(),
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            backgroundColor: '#fee2e2',
                            borderBottom: '1px solid #fca5a5',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4m0 4h.01"
                                    stroke="#dc2626"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span style={{ color: '#991b1b', fontWeight: 500, fontSize: '14px' }}>
                                Diagram Error
                            </span>
                        </div>
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                        <p style={{ color: '#991b1b', fontSize: '13px', margin: 0 }}>
                            {renderState.message}
                        </p>
                    </div>
                </div>
            </NodeViewWrapper>
        );
    }

    // Render loading state
    if (renderState.status === 'loading' || renderState.status === 'idle') {
        return (
            <NodeViewWrapper>
                <div
                    className="excalidraw-container"
                    style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '24px',
                        minHeight: '80px',
                        backgroundColor: '#f9fafb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        margin: getMarginStyle(),
                    }}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ animation: 'spin 1s linear infinite' }}
                    >
                        <circle cx="12" cy="12" r="10" stroke="#d1d5db" strokeWidth="3" fill="none" />
                        <path
                            d="M12 2a10 10 0 019.798 7.853"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeLinecap="round"
                            fill="none"
                        />
                    </svg>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading diagram...</span>
                    <style>{`
                        @keyframes spin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            </NodeViewWrapper>
        );
    }

    // Success state - render the diagram
    return (
        <NodeViewWrapper>
            <div
                ref={containerRef}
                className="excalidraw-container"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => !isResizing && setIsHovered(false)}
                style={{
                    position: 'relative',
                    width: `${dimensions.width}px`,
                    height: `${dimensions.height}px`,
                    margin: getMarginStyle(),
                    border: isResizing
                        ? '2px solid #6366f1'
                        : selected
                          ? '2px solid #3b82f6'
                          : isHovered
                            ? '1px solid #e0e0e0'
                            : '1px solid transparent',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: isResizing ? 'nwse-resize' : 'default',
                    transition: 'border-color 0.15s ease-in-out',
                    boxShadow: selected ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                }}
            >
                {/* Diagram Canvas - View Only */}
                <div
                    style={{
                        height: '100%',
                        backgroundColor: excalidrawData?.appState?.viewBackgroundColor || '#ffffff',
                    }}
                >
                    {renderState.status === 'success' && renderState.svgContent ? (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '16px',
                            }}
                            dangerouslySetInnerHTML={{ __html: renderState.svgContent }}
                        />
                    ) : null}
                </div>

                {/* Resize Handles - only show on hover or when resizing */}
                {(isHovered || isResizing) && (
                    <>
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 'se')}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#6366f1',
                                cursor: 'nwse-resize',
                                borderRadius: '0 0 8px 0',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize"
                        />
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 'e')}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                right: 0,
                                width: '8px',
                                height: '40px',
                                backgroundColor: '#6366f1',
                                cursor: 'ew-resize',
                                transform: 'translateY(-50%)',
                                borderRadius: '0 4px 4px 0',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize width"
                        />
                        <div
                            onMouseDown={(e) => handleMouseDown(e, 's')}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                width: '40px',
                                height: '8px',
                                backgroundColor: '#6366f1',
                                cursor: 'ns-resize',
                                transform: 'translateX(-50%)',
                                borderRadius: '0 0 4px 4px',
                                opacity: isResizing ? 1 : 0.7,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                            title="Drag to resize height"
                        />
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
}

/**
 * Custom Tiptap extension for Excalidraw diagrams
 *
 * Renders Excalidraw JSON as interactive/static diagrams
 */
export const ExcalidrawExtension = Node.create({
    name: 'excalidraw',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            data: {
                default: null,
                parseHTML: element => {
                    const dataAttr = element.getAttribute('data-excalidraw-data');
                    return dataAttr || undefined;
                },
            },
            width: {
                default: 700,
                parseHTML: element => {
                    const width = element.getAttribute('data-width');
                    return width ? parseInt(width, 10) : undefined;
                },
            },
            height: {
                default: 400,
                parseHTML: element => {
                    const height = element.getAttribute('data-height');
                    return height ? parseInt(height, 10) : undefined;
                },
            },
            textAlign: {
                default: 'left',
                parseHTML: element => element.getAttribute('data-align') || undefined,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-excalidraw]',
                priority: 100,
                getAttrs: (dom) => {
                    if (dom instanceof HTMLElement) {
                        const data = dom.getAttribute('data-excalidraw-data') || '';
                        const width = dom.getAttribute('data-width');
                        const height = dom.getAttribute('data-height');
                        const textAlign = dom.getAttribute('data-align') || 'left';

                        if (!data) {
                            console.warn('[Excalidraw Extension] No data found in div[data-excalidraw]');
                            return false;
                        }

                        console.log('[Excalidraw Extension] Parsing div[data-excalidraw]');

                        return {
                            data,
                            width: width ? parseInt(width, 10) : 700,
                            height: height ? parseInt(height, 10) : 400,
                            textAlign,
                        };
                    }
                    return false;
                },
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const width = node.attrs.width || 700;
        const height = node.attrs.height || 400;

        console.log('[Excalidraw Extension] renderHTML called:', {
            width,
            height,
            hasData: !!node.attrs.data,
            align: node.attrs.textAlign,
        });

        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-excalidraw': '',
                'data-excalidraw-data': node.attrs.data || '',
                'data-width': String(width),
                'data-height': String(height),
                'data-align': node.attrs.textAlign || 'left',
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ExcalidrawNodeView);
    },

    addCommands() {
        return {
            setExcalidraw:
                (data: string) =>
                ({ commands }: { commands: any }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: { data },
                    });
                },
        } as any;
    },
});

/**
 * Parse content and extract Excalidraw blocks from backend marker format
 *
 * Backend format (with code fence):
 * <!-- EXCALIDRAW_START -->
 * ```json
 * { ... excalidraw data ... }
 * ```
 * <!-- EXCALIDRAW_END -->
 *
 * Backend format (without code fence):
 * <!-- EXCALIDRAW_START -->
 * { ... excalidraw data ... }
 * <!-- EXCALIDRAW_END -->
 *
 * Error format:
 * <!-- EXCALIDRAW_ERROR: Error message here -->
 */
export function parseExcalidrawBlocks(content: string): string {
    if (!content) return content;

    const START_MARKER = '<!-- EXCALIDRAW_START -->';
    const END_MARKER = '<!-- EXCALIDRAW_END -->';
    const errorPattern = /<!-- EXCALIDRAW_ERROR:\s*(.*?)\s*-->/g;

    let result = content;

    // Helper function to convert Excalidraw JSON to HTML node
    const convertToHtmlNode = (jsonData: string): string => {
        try {
            const trimmedData = jsonData.trim();
            // Parse and validate JSON
            const excalidrawData = JSON.parse(trimmedData);

            console.log('[Excalidraw Parser] Converting Excalidraw block to HTML node, data length:', trimmedData.length);

            // Calculate dimensions from Excalidraw elements
            let width = 700; // default
            let height = 400; // default

            if (excalidrawData.elements && Array.isArray(excalidrawData.elements) && excalidrawData.elements.length > 0) {
                // Calculate bounding box from all elements
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

                excalidrawData.elements.forEach((el: any) => {
                    if (el.x !== undefined && el.y !== undefined && el.width !== undefined && el.height !== undefined) {
                        minX = Math.min(minX, el.x);
                        minY = Math.min(minY, el.y);
                        maxX = Math.max(maxX, el.x + el.width);
                        maxY = Math.max(maxY, el.y + el.height);
                    }
                });

                if (isFinite(minX) && isFinite(maxX) && isFinite(minY) && isFinite(maxY)) {
                    width = Math.ceil(maxX - minX) + 40; // Add padding
                    height = Math.ceil(maxY - minY) + 40; // Add padding
                    console.log(`[Excalidraw Parser] Calculated dimensions: ${width}x${height} from bounding box`);
                }
            }

            // Return HTML that TipTap's Excalidraw extension will parse
            return `<div data-excalidraw data-excalidraw-data="${encodeHTMLAttribute(trimmedData)}" data-width="${width}" data-height="${height}" data-align="left"></div>`;
        } catch (e) {
            console.error('[Excalidraw Parser] Failed to parse Excalidraw JSON:', e);
            return `<p><em>[Excalidraw Error: Invalid diagram data]</em></p>`;
        }
    };

    // Process all Excalidraw blocks by finding start/end markers
    let searchStart = 0;
    while (true) {
        const startIdx = result.indexOf(START_MARKER, searchStart);
        if (startIdx === -1) break;

        const endIdx = result.indexOf(END_MARKER, startIdx + START_MARKER.length);
        if (endIdx === -1) break;

        // Extract content between markers
        let innerContent = result.slice(startIdx + START_MARKER.length, endIdx).trim();

        console.log('[Excalidraw Parser] Found block, inner content starts with:', innerContent.substring(0, 50));

        // Remove code fence if present
        if (innerContent.startsWith('```')) {
            // Find the end of the opening fence line
            const fenceEndIdx = innerContent.indexOf('\n');
            if (fenceEndIdx !== -1) {
                innerContent = innerContent.slice(fenceEndIdx + 1);
            }
            // Remove closing fence
            const closingFenceIdx = innerContent.lastIndexOf('```');
            if (closingFenceIdx !== -1) {
                innerContent = innerContent.slice(0, closingFenceIdx);
            }
            innerContent = innerContent.trim();
            console.log('[Excalidraw Parser] Removed code fence, content now starts with:', innerContent.substring(0, 50));
        }

        // Convert to HTML node
        const htmlNode = convertToHtmlNode(innerContent);

        // Replace the entire block (including markers) with the HTML node
        const fullBlock = result.slice(startIdx, endIdx + END_MARKER.length);
        result = result.slice(0, startIdx) + htmlNode + result.slice(endIdx + END_MARKER.length);

        // Continue searching after the replaced content
        searchStart = startIdx + htmlNode.length;
    }

    // Replace error blocks with formatted error messages
    result = result.replace(errorPattern, (match, errorMessage) => {
        console.log('[Excalidraw Parser] Found error block:', errorMessage);
        return `<p><code>[Diagram Error: ${escapeHtml(errorMessage)}]</code></p>`;
    });

    return result;
}

/**
 * Serialize Excalidraw nodes back to the marker format for backend storage
 */
export function serializeExcalidrawBlocks(html: string): string {
    if (!html) return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const excalidrawNodes = doc.querySelectorAll('div[data-excalidraw]');

    excalidrawNodes.forEach(node => {
        const data = node.getAttribute('data-excalidraw-data');
        if (data) {
            try {
                // Decode and format the JSON
                const decodedData = decodeHTMLAttribute(data);
                const formattedJson = JSON.stringify(JSON.parse(decodedData), null, 2);

                // Create the marker format
                const marker = `<!-- EXCALIDRAW_START -->\n\`\`\`json\n${formattedJson}\n\`\`\`\n<!-- EXCALIDRAW_END -->`;

                // Replace node with text node containing the marker
                const textNode = doc.createTextNode(marker);
                node.replaceWith(textNode);
            } catch (e) {
                console.error('[Excalidraw Serializer] Failed to serialize:', e);
            }
        }
    });

    return doc.body.innerHTML;
}

// Helper function to encode string for HTML attribute
function encodeHTMLAttribute(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// Helper function to decode HTML attribute
function decodeHTMLAttribute(str: string): string {
    return str
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
}

// Helper function to escape HTML
function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
